import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import React, { ComponentProps, useContext, useMemo } from 'react'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { extractMarketingTaglinePrefix, isUltraLuxOffer, TAGLINE_PREFIX_REGEX } from 'lib/offer/offerUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusHiddenOfferCarouselCard from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferCarouselCard'
import { connect } from 'react-redux'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { isHotelPackage } from 'lib/offer/offerTypes'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { take } from 'lib/array/arrayUtils'
import OfferInclusionChipItem from 'components/Common/Inclusions/OfferInclusionChipItem'
import LoyaltyStayUpgradeBenefitPill from 'loyalty/components/LoyaltyStayUpgradeBenefitPill'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import GeoContext from 'contexts/geoContext'

interface MappedProps {
  isLuxPlusEnabled: boolean
  isLuxPlusHiddenOffer: boolean
  inclusionsViewableLuxPlusTier: App.MembershipTier
}

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends MappedProps, InheritedCarouselCardMediumProps {
  offer: App.HotelOffer | App.HotelOfferSummary
  onClick?: () => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function LEOfferCard(props: Props) {
  const {
    offer,
    size,
    onClick,
    className,
    offerUrl,
    isLuxPlusHiddenOffer,
    isLuxPlusEnabled,
    selectedDatesAndRoomsFilter,
    inclusionsViewableLuxPlusTier,
    variant,
    eagerLoadImage,
  } = props
  const { lowestPricePackage } = offer
  const isUltraLux = isUltraLuxOffer(offer)
  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const luxPlusInclusions = lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier as App.MembershipSubscriptionTier]
  const offerLabels = useMemo(() => offer.urgencyTags.filter(label => label.type !== 'left'), [offer])

  // Only includes offer with tagline like "[hotel name] with [inclusion 1], [inclusion 2]..."
  const hasTaglinePrefixOfferName = TAGLINE_PREFIX_REGEX.test(offer.name)
  const inclusionTaglinePrefixVariant = useOptimizelyExperiment(OptimizelyExperiments.croInclusionChipsOnHomepageHeroCarousels, hasTaglinePrefixOfferName)
  const isInclusionTaglinePrefixEnabled = inclusionTaglinePrefixVariant === 'text_variant' || inclusionTaglinePrefixVariant === 'chip_variant'
  const showOnlyTaglinePrefix = isInclusionTaglinePrefixEnabled && hasTaglinePrefixOfferName

  const InclusionComponent = useMemo(() => {
    if (inclusionTaglinePrefixVariant === 'text_variant') return OfferInclusionItem
    if (inclusionTaglinePrefixVariant === 'chip_variant') return OfferInclusionChipItem
    return null
  }, [inclusionTaglinePrefixVariant])

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter || {})
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const inclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.tileInclusions?.length) {
      return offer.tileInclusions
    }
    if (offer.lowestPricePackage?.inclusions?.length) {
      return offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
    }
    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    return pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate') || []
  }, [offer])

  const shortInclusions = useMemo(() => take(inclusions.filter((inclusion) => inclusion.description.length <= 30), 3), [inclusions])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (offer.bundledWithFlightsOnly) {
      return []
    }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: isHotelPackage(pkg) ? pkg : undefined,
        price: pkg.price,
        memberPrice: pkg.memberPrice,
        currentRegionCode,
      }),
    ]
  }, [offer, pkg, currentRegionCode])

  const hasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  let imageCornerLabel: React.ReactElement | undefined = undefined
  if (isUltraLux) {
    imageCornerLabel = <ProductTypeLabel productType={offer.productType} />
  }

  let urgencyTagLabel: React.ReactElement | undefined = undefined
  const urgencyTagAlreadyActive = offerLabels.find((ol: App.OfferUrgencyLabel) => ol.type == 'left' && ol.end || ol.type == 'lux_plus_general_release')
  if (offer?.visibilitySchedule?.end && !urgencyTagAlreadyActive) {
    urgencyTagLabel = <OfferUrgencyCountdownLabel endDate={offer?.visibilitySchedule?.end} tagPlace="offer-card" fallback={imageCornerLabel} />
  }

  const title = showOnlyTaglinePrefix ? extractMarketingTaglinePrefix(offer.name) : offer.name
  const inclusionsElement = showOnlyTaglinePrefix && InclusionComponent ? <Group
    gap={4}
    tabletGap={inclusionTaglinePrefixVariant === 'chip_variant' ? 8 : 12}
    direction="vertical"
    tabletDirection="horizontal"
    horizontalAlign="start"
    wrap="wrap"
  >
    {shortInclusions.map((inclusion) => <InclusionComponent
      key={inclusion.id}
      inclusion={inclusion}
      size={inclusionTaglinePrefixVariant === 'chip_variant' ? 'small' : 'medium'}
    />)}
  </Group> : undefined

  return (
    <ProductPaletteProvider product={offer}>
      {isLuxPlusHiddenOffer && <LuxPlusHiddenOfferCarouselCard urgencyLabels={offerLabels} offer={offer} size={size} />}
      {!isLuxPlusHiddenOffer && <>
        {size === 'medium' && <CarouselCardMedium
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
          providerName={offer.property.name}
          title={title}
          priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          urgencyLabels={<LabelGroup>
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
            {urgencyTagLabel}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          onClick={onClick}
        />}
        {size === 'small' && <CarouselCardSmall
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
          description={offer.name}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
          title={offer.property.name}
          priceDetails={<LEOfferPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} />}
          to={offerUrl}
          productType={offer.productType}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          urgencyLabels={<LabelGroup>
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {urgencyTagLabel}
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          onClick={onClick}
          imageCornerLabel={imageCornerLabel}
        />}
      </>}
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>): MappedProps {
  return ({
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLuxPlusHiddenOffer: getIsLuxPlusHiddenOffer(state, ownProps.offer),
    inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(state),
  })
}

export default connect(mapStateToProps)(LEOfferCard)
